import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, Users, GraduationCap, ChevronRight, Mail, Eye, EyeOff, Loader2, AlertCircle, Sparkles, Crown, Diamond, Gem, Wrench, Server, Code, HardDrive, Shield, Award, Zap, Cloud, Star } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { useTenantContext } from '../../context/TenantContext';
import { useToast } from '../../hooks/useToast';
import { instructorAuthService } from '../../services/instructorAuthService';
import { 
  sendStudentOTP, 
  verifyStudentOTP, 
  generateStudentAccessToken, 
  getStudentProfileByToken,
  type ElearningStudentProfile
} from '../../services/elearningStudentService';

// Mapping d'icônes pour personnalisation
const iconMap: Record<string, React.ElementType> = {
  BookOpen,
  Sparkles,
  Star,
  Diamond,
  Gem,
  Crown,
  Wrench,
  Server,
  Code,
  HardDrive,
  Shield,
  Award,
  Zap,
  Cloud,
};

function hexToRgba(hex: string, alpha = 1) {
  const cleaned = hex.replace('#', '');
  const bigint = parseInt(cleaned.length === 3
    ? cleaned.split('').map((c) => c + c).join('')
    : cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const UnifiedAuthPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Récupérer le paramètre de redirection depuis l'URL
  const searchParams = new URLSearchParams(location.search);
  const redirectPath = searchParams.get('redirect');
  const { tenant, previewConfig } = useTenantContext();

  // Thème UnifiedAuth (preview > tenant)
  const authTheme = React.useMemo(() => {
    const base = (previewConfig?.theme?.notFound) ?? (tenant?.config?.theme?.notFound) ?? ({} as any);
    const themeColors = {
      c1: (tenant?.config?.color1 as string) || '#fe8d17', // primaire (bouton)
      c2: (tenant?.config?.color2 as string) || '#008aef', // secondaire (bouton)
      c3: (tenant?.config?.color3 as string) || '#ffffff',
      c4: (tenant?.config?.color4 as string) || '#9ca3af',
    };
    const defaults = {
      overlay: {
        enabled: true,
        useGradient: true,
        orientation: 'to-b', // to-b, to-t, to-r, to-l, to-tr, to-br, to-tl, to-bl
        color1: '#08062b',
        color2: '#08062b',
        color3: '#08062b',
        opacity1: 0.95,
        opacity2: 0.80,
        opacity3: 0.95,
      },
      icons: [
        { enabled: true, icon: 'BookOpen', color: themeColors.c1, size: 32 }, // top-left
        { enabled: true, icon: 'Sparkles',   color: themeColors.c2, size: 28 }, // top-right
        { enabled: true, icon: 'Diamond',    color: themeColors.c2, size: 24 }, // bottom-left
        { enabled: true, icon: 'Star',       color: themeColors.c1, size: 30 }, // bottom-right
      ]
    } as any;
    return {
      overlay: {
        enabled: base.overlay?.enabled ?? defaults.overlay.enabled,
        useGradient: base.overlay?.useGradient ?? defaults.overlay.useGradient,
        orientation: base.overlay?.orientation ?? defaults.overlay.orientation,
        color1: base.overlay?.color1 ?? defaults.overlay.color1,
        color2: base.overlay?.color2 ?? defaults.overlay.color2,
        color3: base.overlay?.color3 ?? defaults.overlay.color3,
        opacity1: base.overlay?.opacity1 ?? defaults.overlay.opacity1,
        opacity2: base.overlay?.opacity2 ?? defaults.overlay.opacity2,
        opacity3: base.overlay?.opacity3 ?? defaults.overlay.opacity3,
      },
      icons: (base.icons && Array.isArray(base.icons) && base.icons.length === 4)
        ? base.icons.map((i: any, idx: number) => ({
            enabled: i?.enabled ?? defaults.icons[idx].enabled,
            icon: i?.icon ?? defaults.icons[idx].icon,
            color: i?.color ?? defaults.icons[idx].color,
            size: Number(i?.size ?? defaults.icons[idx].size),
          }))
        : defaults.icons,
    };
  }, [tenant, previewConfig]);
  const { toast } = useToast();
  
  // État général
  const [selectedRole, setSelectedRole] = useState<'student' | 'instructor' | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [error, setError] = useState('');
  
  // États pour étudiant (OTP)
  const [studentEmail, setStudentEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  
  // États pour instructeur (email/password)
  const [instructorData, setInstructorData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  
  // Vérifier authentification existante
  useEffect(() => {
    const checkAuth = async () => {
      setIsCheckingAuth(true);
      
      try {
        // Vérifier token étudiant
        const studentToken = localStorage.getItem('elearning_student_token');
        if (studentToken) {
          try {
            const profile = await getStudentProfileByToken(studentToken);
            if (profile) {
              // Propagation SSO: TOUJOURS synchroniser les tokens (même si déjà présents)
              // Cela garantit que tous les systèmes utilisent le même token actif
              localStorage.setItem('elearning_student_token', studentToken);
              localStorage.setItem('customer_access_token', studentToken);
              localStorage.setItem('psy_patient_token', studentToken);
              
              // Stocker le domaine pour référence
              const currentDomain = window.location.hostname;
              localStorage.setItem('sso_domain', currentDomain);
              
              // Étudiant déjà connecté - redirection vers son dashboard
              navigate('/elearning/student/dashboard');
              return;
            }
          } catch (error) {
            // Token invalide - le supprimer
            localStorage.removeItem('elearning_student_token');
          }
        }
        
        // Vérifier auth instructeur via service direct
        try {
          const instructorAuth = await instructorAuthService.getCurrentUser();
          if (instructorAuth) {
            // Instructeur déjà connecté - redirection vers son dashboard
            navigate('/instructor/dashboard');
            return;
          }
        } catch (error) {
          // Pas d'authentification instructeur
        }
      } finally {
        setIsCheckingAuth(false);
      }
    };
    
    checkAuth();
  }, [navigate]);
  
  // Gestion étudiant - Envoyer OTP
  const handleSendOTP = async () => {
    if (!studentEmail) {
      setError('Veuillez entrer votre adresse email');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      const result = await sendStudentOTP(studentEmail);
      if (result) {
        toast({
          title: 'Code envoyé',
          description: 'Un code de vérification a été envoyé à votre adresse email',
          variant: 'default'
        });
        setIsVerifying(true);
      } else {
        throw new Error('Impossible d\'envoyer le code OTP');
      }
    } catch (error) {
      setError('Impossible d\'envoyer le code de vérification');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Gestion étudiant - Vérifier OTP
  const handleVerifyOTP = async () => {
    if (!otp) {
      setError('Veuillez entrer le code de vérification');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      const isValid = verifyStudentOTP(studentEmail, otp);
      if (isValid) {
        const token = await generateStudentAccessToken(studentEmail);
        if (token) {
          localStorage.setItem('elearning_student_token', token);
          // Propagation SSO: synchroniser les tokens shop et psy
          localStorage.setItem('customer_access_token', token);
          localStorage.setItem('psy_patient_token', token);
          // Stocker le domaine pour référence
          const currentDomain = window.location.hostname;
          localStorage.setItem('sso_domain', currentDomain);
          const profile = await getStudentProfileByToken(token);
          if (profile) {
            toast({
              title: 'Connexion réussie',
              description: 'Vous êtes maintenant connecté à votre compte étudiant',
              variant: 'default'
            });
            navigate(redirectPath || '/elearning/student/dashboard');
          }
        }
      } else {
        setError('Code de vérification incorrect');
      }
    } catch (error) {
      setError('Erreur lors de la vérification du code');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Gestion instructeur - Connexion
  const handleInstructorLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      const result = await instructorAuthService.login(instructorData.email, instructorData.password);
      if (result.success && result.user) {
        toast({
          title: 'Connexion réussie',
          description: 'Vous êtes maintenant connecté à votre compte instructeur',
          variant: 'default'
        });
        // Les instructeurs gardent leur redirection par défaut
        navigate('/instructor/dashboard');
      } else {
        throw new Error(result.error || 'Erreur de connexion');
      }
    } catch (err: any) {
      let errorMessage = 'Erreur de connexion';
      if (err.message.includes('Invalid login credentials') || err.message.includes('invalid')) {
        errorMessage = 'Email ou mot de passe incorrect';
      } else if (err.message.includes('Email not confirmed')) {
        errorMessage = 'Veuillez confirmer votre email avant de vous connecter';
      } else {
        errorMessage = err.message || 'Une erreur est survenue lors de la connexion';
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleRoleSelection = (role: 'student' | 'instructor') => {
    setSelectedRole(role);
    setError('');
    setIsVerifying(false);
    setOtp('');
    setStudentEmail('');
    setInstructorData({ email: '', password: '' });
  };

  return (
    <PublicLayout>
      <SEOMetaTags 
        title="Connexion - E-Learning"
        description="Connectez-vous à votre compte étudiant ou instructeur"
        keywords="connexion, login, étudiant, instructeur, e-learning"
      />

      <div className="min-h-screen relative overflow-hidden">
        {/* Fond avec la nouvelle palette de couleurs SIS */}
        <div 
          className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url(https://images.pexels.com/photos/326503/pexels-photo-326503.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2)',
            backgroundAttachment: 'fixed'
          }}
        >
          {authTheme.overlay.enabled && (
            <div className="absolute inset-0 backdrop-blur-[2px]" style={{
              backgroundImage: authTheme.overlay.useGradient
                ? `linear-gradient(${(
                    {
                      'to-b': 'to bottom',
                      'to-t': 'to top',
                      'to-r': 'to right',
                      'to-l': 'to left',
                      'to-tr': 'to top right',
                      'to-br': 'to bottom right',
                      'to-tl': 'to top left',
                      'to-bl': 'to bottom left',
                    } as Record<string, string>
                  )[authTheme.overlay.orientation] || 'to bottom'},
                    ${hexToRgba(authTheme.overlay.color1, authTheme.overlay.opacity1)},
                    ${hexToRgba(authTheme.overlay.color2, authTheme.overlay.opacity2)},
                    ${hexToRgba(authTheme.overlay.color3, authTheme.overlay.opacity3)}
                  )`
                : undefined,
              backgroundColor: !authTheme.overlay.useGradient
                ? hexToRgba(authTheme.overlay.color1, authTheme.overlay.opacity2)
                : undefined,
            }} />
          )}
        </div>
        
        {/* Particules flottantes avec couleurs et icônes personnalisables */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Top-Left */}
          {authTheme.icons[0]?.enabled && (
            <motion.div
              className="absolute top-20 left-10"
              style={{ color: hexToRgba(authTheme.icons[0].color, 0.2) }}
              animate={{ y: [-20, 20, -20], rotate: [0, 180, 360] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            >
              {React.createElement(iconMap[authTheme.icons[0].icon] || BookOpen, { size: authTheme.icons[0].size })}
            </motion.div>
          )}
          {/* Top-Right */}
          {authTheme.icons[1]?.enabled && (
            <motion.div
              className="absolute top-40 right-20"
              style={{ color: hexToRgba(authTheme.icons[1].color, 0.2) }}
              animate={{ y: [20, -20, 20], rotate: [360, 180, 0] }}
              transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            >
              {React.createElement(iconMap[authTheme.icons[1].icon] || Sparkles, { size: authTheme.icons[1].size })}
            </motion.div>
          )}
          {/* Bottom-Left */}
          {authTheme.icons[2]?.enabled && (
            <motion.div
              className="absolute bottom-40 left-20"
              style={{ color: hexToRgba(authTheme.icons[2].color, 0.2) }}
              animate={{ y: [-15, 15, -15], rotate: [0, 90, 180] }}
              transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            >
              {React.createElement(iconMap[authTheme.icons[2].icon] || Diamond, { size: authTheme.icons[2].size })}
            </motion.div>
          )}
          {/* Bottom-Right */}
          {authTheme.icons[3]?.enabled && (
            <motion.div
              className="absolute bottom-20 right-10"
              style={{ color: hexToRgba(authTheme.icons[3].color, 0.2) }}
              animate={{ y: [15, -15, 15], rotate: [180, 270, 360] }}
              transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
            >
              {React.createElement(iconMap[authTheme.icons[3].icon] || Star, { size: authTheme.icons[3].size })}
            </motion.div>
          )}
        </div>

        <div className="relative z-20 flex items-center justify-center min-h-screen py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl w-full space-y-8">
          
          {/* Écran de chargement pendant vérification d'auth */}
          {isCheckingAuth ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center justify-center space-y-6"
            >
              <div className="relative">
                <div className="w-16 h-16 border-4 rounded-full animate-spin"
                  style={{
                    borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2),
                    borderTopColor: tenant?.config?.color1 || '#fe8d17'
                  }}
                ></div>
                <div className="absolute inset-0 w-16 h-16 border-4 border-transparent rounded-full animate-spin" 
                  style={{ 
                    borderTopColor: hexToRgba(tenant?.config?.color2 || '#008aef', 0.3),
                    animationDirection: 'reverse',
                    animationDuration: '1.5s'
                  }}
                ></div>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-2" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Vérification en cours...</h3>
                <p style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.7) }}>Nous vérifions si vous êtes déjà connecté</p>
              </div>
            </motion.div>
          ) : (
            <>
          
          {/* En-tête */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="flex justify-center mb-6">
              {tenant?.logo ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="relative group"
                >
                  {/* Effet de halo lumineux */}
                  <div className="absolute -inset-4 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                      backgroundImage: `linear-gradient(to right, ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2)}, ${hexToRgba(tenant?.config?.color3 || '#ffffff', 0.1)}, ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2)})`
                    }}
                  />
                  
                  {/* Container du logo avec bordure élégante */}
                  <div className="relative p-3 bg-white/10 backdrop-blur-sm rounded-2xl border shadow-2xl transition-all duration-500 hover:scale-105"
                    style={{
                      borderColor: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.2),
                      boxShadow: `0 25px 50px -12px ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.25)}`
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = `0 0 30px ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.3)}`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = `0 25px 50px -12px ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.25)}`;
                    }}
                  >
                    <img 
                      src={tenant.logo} 
                      alt={tenant.name} 
                      className="h-16 w-auto filter drop-shadow-lg"
                    />
                    
                    {/* Effet de brillance qui traverse */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 -skew-x-12 transform rounded-2xl" />
                  </div>
                  
                  {/* Particules décoratives */}
                  <div className="absolute -top-1 -right-1 w-2 h-2 rounded-full animate-pulse opacity-60" style={{ backgroundColor: tenant?.config?.color1 || '#fe8d17' }} />
                  <div className="absolute -bottom-1 -left-1 w-1.5 h-1.5 rounded-full animate-ping" style={{ backgroundColor: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.6) }} />
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="relative group"
                >
                  <div className="p-3 backdrop-blur-sm rounded-2xl border shadow-2xl transition-all duration-500 hover:scale-105"
                    style={{
                      backgroundColor: hexToRgba(tenant?.config?.color2 || '#008aef', 0.2),
                      borderColor: hexToRgba(tenant?.config?.color2 || '#008aef', 0.3),
                      boxShadow: `0 25px 50px -12px ${hexToRgba(tenant?.config?.color2 || '#008aef', 0.25)}`
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = `0 0 30px ${hexToRgba(tenant?.config?.color2 || '#008aef', 0.3)}`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = `0 25px 50px -12px ${hexToRgba(tenant?.config?.color2 || '#008aef', 0.25)}`;
                    }}
                  >
                    <BookOpen className="h-16 w-16 drop-shadow-lg" style={{ color: tenant?.config?.color2 || '#008aef' }} />
                  </div>
                </motion.div>
              )}
            </div>
            <motion.h1 
              className="text-4xl md:text-5xl font-bold mb-4 drop-shadow-lg"
              style={{ color: tenant?.config?.color3 || '#ffffff' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Bienvenue sur {tenant?.name || 'E-Learning'}
            </motion.h1>
            <motion.p 
              className="text-xl mb-2 drop-shadow-md"
              style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.9) }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              {!selectedRole ? 'Choisissez votre type de compte pour vous connecter' : 'Connectez-vous à votre compte'}
            </motion.p>
            

            <motion.div
              className="w-24 h-1 mx-auto rounded-full"
              style={{
                backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color2 || '#008aef'})`
              }}
              initial={{ width: 0 }}
              animate={{ width: 96 }}
              transition={{ duration: 1, delay: 0.8 }}
            />
          </motion.div>

          {!selectedRole ? (
            <div className="space-y-8">
              {/* Sélection du rôle */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >
            {/* Carte Étudiant */}
            <Card 
              className="cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-105 border-2 bg-white/5 backdrop-blur-sm"
              style={{
                borderColor: selectedRole === 'student' 
                  ? tenant?.config?.color2 || '#008aef'
                  : hexToRgba(tenant?.config?.color3 || '#ffffff', 0.2),
                backgroundColor: selectedRole === 'student'
                  ? hexToRgba(tenant?.config?.color2 || '#008aef', 0.1)
                  : 'rgba(255, 255, 255, 0.05)'
              }}
              onClick={() => handleRoleSelection('student')}
            >
              <CardHeader className="text-center pb-4">
                <div className="flex justify-center mb-4">
                  <div className="p-4 rounded-full"
                    style={{
                      backgroundColor: hexToRgba(tenant?.config?.color2 || '#008aef', 0.2)
                    }}
                  >
                    <GraduationCap className="h-12 w-12" style={{ color: tenant?.config?.color2 || '#008aef' }} />
                  </div>
                </div>
                <CardTitle className="text-2xl font-bold" style={{ color: tenant?.config?.color3 || '#ffffff' }}>
                  Étudiant
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="mb-6" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }}>
                  Accédez à vos cours, suivez votre progression et interagissez avec vos instructeurs
                </p>
                <div className="space-y-2 text-sm mb-6" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.6) }}>
                  <div className="flex items-center justify-center">
                    <span>• Connexion par email (sans mot de passe)</span>
                  </div>
                  <div className="flex items-center justify-center">
                    <span>• Accès instantané via code OTP</span>
                  </div>
                </div>
                <Button 
                  className="w-full shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
                  style={{
                    backgroundColor: tenant?.config?.color2 || '#008aef',
                    color: tenant?.config?.color3 || '#ffffff',
                    backgroundImage: `linear-gradient(to right, ${tenant?.config?.color2 || '#008aef'}, ${hexToRgba(tenant?.config?.color2 || '#008aef', 0.8)})`
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRoleSelection('student');
                  }}
                >
                  Se connecter comme étudiant
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>

            {/* Carte Instructeur */}
            <Card 
              className="cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-105 border-2 bg-white/5 backdrop-blur-sm"
              style={{
                borderColor: selectedRole === 'instructor' 
                  ? tenant?.config?.color1 || '#fe8d17'
                  : hexToRgba(tenant?.config?.color3 || '#ffffff', 0.2),
                backgroundColor: selectedRole === 'instructor'
                  ? hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.1)
                  : 'rgba(255, 255, 255, 0.05)'
              }}
              onClick={() => handleRoleSelection('instructor')}
            >
              <CardHeader className="text-center pb-4">
                <div className="flex justify-center mb-4">
                  <div className="p-4 rounded-full"
                    style={{
                      backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2)
                    }}
                  >
                    <Users className="h-12 w-12" style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
                  </div>
                </div>
                <CardTitle className="text-2xl font-bold" style={{ color: tenant?.config?.color3 || '#ffffff' }}>
                  Instructeur
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="mb-6" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }}>
                  Gérez vos cours, suivez vos étudiants et créez du contenu pédagogique
                </p>
                <div className="space-y-2 text-sm mb-6" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.6) }}>
                  <div className="flex items-center justify-center">
                    <span>• Connexion sécurisée par mot de passe</span>
                  </div>
                  <div className="flex items-center justify-center">
                    <span>• Accès aux outils d'enseignement</span>
                  </div>
                </div>
                <Button 
                  className="w-full shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
                  style={{
                    backgroundColor: tenant?.config?.color1 || '#fe8d17',
                    color: tenant?.config?.color3 || '#ffffff',
                    backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.8)})`
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRoleSelection('instructor');
                  }}
                >
                  Se connecter comme instructeur
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
            
              </motion.div>
              
              {/* Informations sur la création de comptes - même largeur que les cartes */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.0 }}
                className="w-full"
              >
                <div className="p-6 bg-white/10 backdrop-blur-sm rounded-xl border"
                  style={{ borderColor: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.2) }}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.9) }}>
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: hexToRgba(tenant?.config?.color2 || '#008aef', 0.2) }}
                      >
                        <GraduationCap className="w-5 h-5" style={{ color: tenant?.config?.color2 || '#008aef' }} />
                      </div>
                      <div>
                        <p className="font-semibold mb-2 text-base" style={{ color: tenant?.config?.color2 || '#008aef' }}>Étudiants</p>
                        <p style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }}>Votre compte se crée automatiquement lors de votre premier achat de cours</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2) }}
                      >
                        <Users className="w-5 h-5" style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
                      </div>
                      <div>
                        <p className="font-semibold mb-2 text-base" style={{ color: tenant?.config?.color1 || '#fe8d17' }}>Instructeurs</p>
                        <p style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }}>Contactez l'administration pour obtenir vos accès de connexion</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          ) : (
            /* Formulaires de connexion */
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="max-w-md mx-auto"
            >
              <Card className="bg-white/5 border backdrop-blur-sm text-white"
                style={{
                  borderColor: `${tenant?.config?.color1 || '#fe8d17'}33`
                }}
              >
                <CardHeader className="text-center">
                  <div className="flex justify-center mb-4">
                    <div className="p-3 rounded-full"
                      style={{
                        backgroundColor: selectedRole === 'student' 
                          ? hexToRgba(tenant?.config?.color2 || '#008aef', 0.2)
                          : hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2)
                      }}
                    >
                      {selectedRole === 'student' ? (
                        <GraduationCap className="h-8 w-8" style={{ color: tenant?.config?.color2 || '#008aef' }} />
                      ) : (
                        <Users className="h-8 w-8" style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
                      )}
                    </div>
                  </div>
                  <CardTitle className="text-xl font-bold" style={{ color: tenant?.config?.color3 || '#ffffff' }}>
                    Connexion {selectedRole === 'student' ? 'Étudiant' : 'Instructeur'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {error && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}
                  
                  {selectedRole === 'student' ? (
                    /* Formulaire étudiant (OTP) */
                    <div className="space-y-4">
                      {!isVerifying ? (
                        <>
                          <div className="space-y-3">
                            <label className="text-sm font-semibold" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.9) }}>
                              Adresse email
                            </label>
                            <div className="relative">
                              <Mail className="absolute left-4 top-4 h-5 w-5" style={{ color: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.7) }} />
                              <Input
                                type="email"
                                placeholder="votre@email.com"
                                value={studentEmail}
                                onChange={(e) => setStudentEmail(e.target.value)}
                                className="pl-12 py-3 bg-white/10 border-white/30 placeholder:text-white/60 focus:ring-2 focus:border-transparent rounded-xl backdrop-blur-sm outline-none transition-all duration-300"
                                style={{
                                  color: tenant?.config?.color3 || '#ffffff',
                                  '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                                } as React.CSSProperties}
                                disabled={isLoading}
                              />
                            </div>
                          </div>
                          <Button 
                            onClick={handleSendOTP}
                            disabled={isLoading || !studentEmail}
                            className="w-full font-semibold py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                            style={{
                              backgroundColor: tenant?.config?.color1 || '#fe8d17',
                              color: tenant?.config?.color3 || '#ffffff',
                              backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color2 || '#008aef'})`
                            }}
                          >
                            {isLoading ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Envoi en cours...
                              </>
                            ) : (
                              'Envoyer le code de vérification'
                            )}
                          </Button>
                        </>
                      ) : (
                        <>
                          <div className="text-center text-sm mb-6 p-4 rounded-xl border"
                            style={{
                              color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8),
                              backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.1),
                              borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2)
                            }}
                          >
                            Un code de vérification a été envoyé à <strong style={{ color: tenant?.config?.color1 || '#fe8d17' }}>{studentEmail}</strong>
                          </div>
                          <div className="space-y-3">
                            <label className="text-sm font-semibold" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.9) }}>
                              Code de vérification
                            </label>
                            <Input
                              type="text"
                              placeholder="123456"
                              value={otp}
                              onChange={(e) => setOtp(e.target.value)}
                              className="text-center text-lg tracking-widest bg-white/10 border-white/30 placeholder:text-white/60 focus:ring-2 focus:border-transparent rounded-xl backdrop-blur-sm py-3 outline-none transition-all duration-300"
                              style={{
                                color: tenant?.config?.color3 || '#ffffff',
                                '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                              } as React.CSSProperties}
                              maxLength={6}
                              disabled={isLoading}
                            />
                          </div>
                          <div className="flex space-x-2">
                            <Button 
                              variant="outline"
                              onClick={() => {
                                setIsVerifying(false);
                                setOtp('');
                                setError('');
                              }}
                              className="flex-1"
                              disabled={isLoading}
                            >
                              Retour
                            </Button>
                            <Button 
                              onClick={handleVerifyOTP}
                              disabled={isLoading || !otp}
                              className="flex-1 font-semibold rounded-xl transition-all duration-300"
                              style={{
                                backgroundColor: tenant?.config?.color1 || '#fe8d17',
                                color: tenant?.config?.color3 || '#ffffff',
                                backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color2 || '#008aef'})`
                              }}
                            >
                              {isLoading ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Vérification...
                                </>
                              ) : (
                                'Vérifier'
                              )}
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    /* Formulaire instructeur (email/password) */
                    <form onSubmit={handleInstructorLogin} className="space-y-6">
                      <div className="space-y-3">
                        <label className="text-sm font-semibold" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.9) }}>
                          Adresse email
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-4 top-4 h-5 w-5" style={{ color: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.7) }} />
                          <Input
                            type="email"
                            placeholder="votre@email.com"
                            value={instructorData.email}
                            onChange={(e) => setInstructorData(prev => ({ ...prev, email: e.target.value }))}
                            className="pl-12 py-3 bg-white/10 border-white/30 placeholder:text-white/60 focus:ring-2 focus:border-transparent rounded-xl backdrop-blur-sm outline-none transition-all duration-300"
                            style={{
                              color: tenant?.config?.color3 || '#ffffff',
                              '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                            } as React.CSSProperties}
                            disabled={isLoading}
                            required
                          />
                        </div>
                      </div>
                      <div className="space-y-3">
                        <label className="text-sm font-semibold" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.9) }}>
                          Mot de passe
                        </label>
                        <div className="relative">
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Votre mot de passe"
                            value={instructorData.password}
                            onChange={(e) => setInstructorData(prev => ({ ...prev, password: e.target.value }))}
                            className="pr-12 py-3 bg-white/10 border-white/30 placeholder:text-white/60 focus:ring-2 focus:border-transparent rounded-xl backdrop-blur-sm outline-none transition-all duration-300"
                            style={{
                              color: tenant?.config?.color3 || '#ffffff',
                              '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                            } as React.CSSProperties}
                            disabled={isLoading}
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-4 top-4 transition-colors"
                            style={{
                              color: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.7)
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = tenant?.config?.color1 || '#fe8d17';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.7);
                            }}
                            disabled={isLoading}
                          >
                            {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                          </button>
                        </div>
                      </div>
                      <Button 
                        type="submit"
                        disabled={isLoading || !instructorData.email || !instructorData.password}
                        className="w-full font-semibold py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                        style={{
                          backgroundColor: tenant?.config?.color1 || '#fe8d17',
                          color: tenant?.config?.color3 || '#ffffff',
                          backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color2 || '#008aef'})`
                        }}
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Connexion...
                          </>
                        ) : (
                          'Se connecter'
                        )}
                      </Button>
                    </form>
                  )}
                  
                  <div className="text-center pt-4">
                    <Button 
                      variant="ghost" 
                      onClick={() => handleRoleSelection(null as any)}
                      className="text-sm transition-colors"
                      style={{
                        color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.5)
                      }}
                      onMouseEnter={(e) => {
                        if (!isLoading) {
                          e.currentTarget.style.color = hexToRgba(tenant?.config?.color3 || '#ffffff', 0.7);
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isLoading) {
                          e.currentTarget.style.color = hexToRgba(tenant?.config?.color3 || '#ffffff', 0.5);
                        }
                      }}
                      disabled={isLoading}
                    >
                      ← Changer de type de compte
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          </>
          )}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default UnifiedAuthPage;
